import { useQuery } from '@apollo/client';
import { useTranslation } from 'react-i18next';

import { useApplicationContext } from '../../core/contexts/ApplicationContext.tsx';
import { hasWorkistPermissions } from '../../users/utils.ts';
import { GET_CHAT_USAGE, GET_CONVERSATION } from '../queries.ts';

// Monthly Worki budget of the current user as a percentage (capped at 100 by the backend). Internal users
// (staff, support) see their usage but are never blocked — the same exemption the backend applies.
export const useChatUsage = () => {
    const { user } = useApplicationContext();
    const { i18n } = useTranslation();
    const { data } = useQuery(GET_CHAT_USAGE);
    const percent: number = data?.viewer?.usedMonthlyChatLlmCostPercent ?? 0;
    const limitReached = percent >= 100 && !hasWorkistPermissions(user);
    const now = new Date();
    const resetDate = new Intl.DateTimeFormat(i18n.language, { day: 'numeric', month: 'long' }).format(
        new Date(now.getFullYear(), now.getMonth() + 1, 1)
    );
    return { percent, limitReached, resetDate };
};

// How much of a conversation's context window is used, as a percentage; null without a conversation. Reads the
// cache only: the thread view owns the conversation query and the stream keeps its context tokens fresh there.
export const useChatContextUsage = (conversationId: string | null): number | null => {
    const { data } = useQuery(GET_CONVERSATION, {
        variables: { id: conversationId },
        skip: !conversationId,
        fetchPolicy: 'cache-only',
    });
    const conversation = data?.agentConversation;
    if (!conversation?.contextWindow) return null;
    return Math.round(Math.min(conversation.contextTokens / conversation.contextWindow, 1) * 100);
};
